import graphql__ed918f72c3173a434363 from "./__generated__/ContactAttributesPanel_contact.graphql.ts";
import graphql__83f67b2310e8248e9db5 from "./__generated__/ContactAttributesPanelAttributeDefinitionsQuery.graphql.ts";
import graphql__3586839afd2668d21772 from "./__generated__/ContactAttributesPanelSetContactAttributeMutation.graphql.ts";
import graphql__7cbb7a0e10afe2798bab from "./__generated__/ContactAttributesPanelRemoveContactAttributeMutation.graphql.ts";import * as React from "react";
import {useState} from "react";
import {commitMutation, graphql, useLazyLoadQuery} from "react-relay";
import {useFragment, useRelayEnvironment} from "react-relay/hooks";
import Panel from "./presentational/Panel";
import {useIntl} from "react-intl";
import {Form, Formik} from "formik";
import * as yup from "yup";
import ActionButton from "./presentational/button/ActionButton";
import {faCheck, faPencil, faPlus, faTimes, faTrashCan} from "@fortawesome/pro-regular-svg-icons";
import {connect, ConnectedProps} from "react-redux";
import {hideModal, showModal} from "../store/actions";
import Field from "./form/Field";
import DatePickerField from "./form/DatePickerField";
import Checkbox from "./form/Checkbox";
import {ContactAttributesPanelSetContactAttributeMutation} from "./__generated__/ContactAttributesPanelSetContactAttributeMutation.graphql";
import {ContactAttributesPanelRemoveContactAttributeMutation} from "./__generated__/ContactAttributesPanelRemoveContactAttributeMutation.graphql";
import DeleteContactAttributeDialog from "./dialog/DeleteContactAttributeDialog";
import {useErrorContext} from "../contexts/ErrorContext";
import {commitMutationWithErrorHandling} from "../helper/commitMutationWithErrorHandling";

type Props = {
    queryRef: any;
}

const connector = connect(undefined, {
    dispatchShowModal: showModal,
    dispatchHideModal: hideModal
});

type ContactAttributesPanelProps = Props & ConnectedProps<typeof connector>;


const ContactAttributesPanel: React.FC<ContactAttributesPanelProps> = (props) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {dispatchShowModal, dispatchHideModal} = props;
    const [editingCheckboxValue, setEditingCheckboxValue] = useState<boolean>(false);

    const contact = useFragment(
        graphql__ed918f72c3173a434363,
        props.queryRef
    );

    const attributeDefinitionsQuery = useLazyLoadQuery(
        graphql__83f67b2310e8248e9db5,
        {}
    );

    const [editingAttributeName, setEditingAttributeName] = useState<string | null>(null);
    const [isAddingAttribute, setIsAddingAttribute] = useState<boolean>(false);
    const [selectedAttributeDefinition, setSelectedAttributeDefinition] = useState<string>('');

    // Get available attribute definitions that are not already set on this contact
    const getAvailableAttributeDefinitions = () => {
        const existingAttributeNames = contact.attributes?.map(attr => attr.name) || [];
        return attributeDefinitionsQuery.attributeDefinitions?.edges?.map(edge => edge.node).filter(
            def => def && !existingAttributeNames.includes(def.name)
        ) || [];
    };

    // Get the label for an attribute from its definition
    const getAttributeLabel = (attributeName: string): string => {
        const definition = attributeDefinitionsQuery.attributeDefinitions?.edges?.find(
            edge => edge.node?.name === attributeName
        )?.node;
        return definition?.label || attributeName;
    };

    // Get the type for an attribute from its definition
    const getAttributeType = (attributeName: string): string | null => {
        const definition = attributeDefinitionsQuery.attributeDefinitions?.edges?.find(
            edge => edge.node?.name === attributeName
        )?.node;
        return definition?.type || null;
    };

    // Helper function to convert form values to string for GraphQL
    const convertValueToString = (value: any, attributeType: string): string => {
        if (attributeType === 'BOOLEAN') {
            return value ? 'true' : 'false';
        }
        return String(value);
    };

    const handleAddAttribute = () => {
        setIsAddingAttribute(true);
        setSelectedAttributeDefinition('');
    };

    const handleCancelAddAttribute = () => {
        setIsAddingAttribute(false);
        setSelectedAttributeDefinition('');
    };

    const {addError} = useErrorContext();

    const handleSetAttribute = (attributeName: string, value: string) => {
        commitMutationWithErrorHandling<ContactAttributesPanelSetContactAttributeMutation>(
            relayEnvironment,
            {
                mutation: graphql__3586839afd2668d21772,
                variables: {
                    input: {
                        id: contact.id,
                        attributeName: attributeName,
                        value: value
                    }
                },
                onCompleted: (response, errors) => {
                    if (errors && errors.length > 0) {
                        console.error(errors);
                    }
                    setEditingAttributeName(null);
                },
                onError: (error) => {
                    console.error(error);
                    setEditingAttributeName(null);
                }
            },
            addError,
            {
                customErrorMessage: "Failed to set contact attribute."
            }
        );
    };

    const handleRemoveAttribute = (attributeName: string) => {
        commitMutationWithErrorHandling<ContactAttributesPanelRemoveContactAttributeMutation>(
            relayEnvironment,
            {
                mutation: graphql__7cbb7a0e10afe2798bab,
                variables: {
                    input: {
                        id: contact.id,
                        attributeName: attributeName
                    }
                },
                onCompleted: (response, errors) => {
                    if (errors && errors.length > 0) {
                        console.error(errors);
                    }
                },
                onError: (error) => {
                    console.error(error);
                }
            },
            addError,
            {
                customErrorMessage: "Failed to remove contact attribute."
            }
        );
    };

    const showDeleteAttributeDialog = (attributeName: string) => {
        const attributeLabel = getAttributeLabel(attributeName);
        dispatchShowModal({
            body: (
                <DeleteContactAttributeDialog
                    attributeName={attributeName}
                    attributeLabel={attributeLabel}
                    onConfirm={() => handleRemoveAttribute(attributeName)}
                    handleCloseDialog={dispatchHideModal}
                />
            )
        });
    };

    const renderAddAttributeForm = () => {
        const availableDefinitions = getAvailableAttributeDefinitions();
        const selectedDef = availableDefinitions.find(def => def.name === selectedAttributeDefinition);

        return (
            <div className="flex items-center justify-between py-2 border-b border-gray-200 bg-gray-50">
                <div className="flex-1">
                    <div className="flex items-center space-x-2">
                        <div className="w-64 shrink-0">
                            <Field
                                as="select"
                                name="attributeDefinition"
                                value={selectedAttributeDefinition}
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                    setSelectedAttributeDefinition(e.target.value);
                                }}
                                className="border border-gray-300 rounded-sm px-2 py-1 text-sm"
                            >
                                <option value="">{intl.formatMessage({id: 'contact.attributes.selectAttribute'})}</option>
                                {availableDefinitions.map(def => (
                                    <option key={def.name} value={def.name}>
                                        {def.label} ({def.type})
                                    </option>
                                ))}
                            </Field>
                        </div>
                        {selectedDef && (
                            <>
                                {selectedDef.type === 'STRING' && (
                                    <Field
                                        name="newAttributeValue"
                                        type="text"
                                        placeholder="Enter value..."
                                        className="border border-gray-300 rounded-sm px-2 py-1 text-sm flex-1"
                                        autoFocus
                                    />
                                )}
                                {selectedDef.type === 'INTEGER' && (
                                    <Field
                                        name="newAttributeValue"
                                        type="number"
                                        placeholder="Enter number..."
                                        className="border border-gray-300 rounded-sm px-2 py-1 text-sm flex-1"
                                        autoFocus
                                    />
                                )}
                                {selectedDef.type === 'BOOLEAN' && (
                                    <Checkbox
                                        name="newAttributeValue"
                                        label="True"
                                    />
                                )}
                                {selectedDef.type === 'DATE' && (
                                    <DatePickerField
                                        name="newAttributeValue"
                                        className="flex-1 min-w-0"
                                        showTimeSelect={true}
                                        timeIntervals={15}
                                    />
                                )}
                            </>
                        )}

                        <ActionButton
                            icon={faCheck}
                            title={intl.formatMessage({id: 'button.save'})}
                            type="submit"
                            additionalClassname="text-xs p-1"
                        />
                        <ActionButton
                            icon={faTimes}
                            title={intl.formatMessage({id: 'button.cancel'})}
                            onClick={handleCancelAddAttribute}
                            additionalClassname="text-xs p-1"
                        />
                    </div>
                </div>
            </div>
        );
    };

    const renderAttributeValue = (attribute: any) => {
        const isEditing = editingAttributeName === attribute.name;
        const attributeType = getAttributeType(attribute.name);

        if (!isEditing) {
            let displayValue = attribute.value;
            if (attributeType === 'BOOLEAN') {
                displayValue = attribute.value === 'true' ? '✓' : '✗';
            } else if (attributeType === 'DATE') {
                try {
                    const date = new Date(attribute.value);
                    displayValue = date.toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    });
                } catch (e) {
                    displayValue = attribute.value;
                }
            }

            return (
                <div className="flex items-center justify-between group">
                    <span className="text-sm text-gray-900">{displayValue}</span>
                    <div className="flex space-x-2 invisible group-hover:visible">
                        {attributeType !== null && (<ActionButton
                            icon={faPencil}
                            title={intl.formatMessage({id: 'contact.attributes.editAttribute'})}
                            onClick={() => {
                                setEditingAttributeName(attribute.name);
                                if (attributeType === 'BOOLEAN') {
                                    setEditingCheckboxValue(attribute.value === 'true');
                                }
                            }}
                            additionalClassname="text-xs p-1"
                        />)}
                        <ActionButton
                            icon={faTrashCan}
                            title={intl.formatMessage({id: 'contact.attributes.deleteAttribute'})}
                            onClick={() => showDeleteAttributeDialog(attribute.name)}
                            additionalClassname="text-xs p-1"
                        />
                    </div>
                </div>
            );
        } else {
            // Edit mode
            return (
                <div className="flex items-center space-x-2">
                    {attributeType === 'STRING' && (
                        <Field
                            name="editValue"
                            type="text"
                            defaultValue={attribute.value || ''}
                            className="border border-gray-300 rounded-sm px-2 py-1 text-sm flex-1"
                            autoFocus
                        />
                    )}
                    {attributeType === 'INTEGER' && (
                        <Field
                            name="editValue"
                            type="number"
                            defaultValue={attribute.value || ''}
                            className="border border-gray-300 rounded-sm px-2 py-1 text-sm flex-1"
                            autoFocus
                        />
                    )}
                    {attributeType === 'BOOLEAN' && (
                        <div className="grow">
                            <input
                                type="checkbox"
                                name="editValue"
                                checked={editingCheckboxValue}
                                onChange={(e) => setEditingCheckboxValue(e.target.checked)}
                                className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300 rounded-sm"
                            />
                        </div>
                    )}
                    {attributeType === 'DATE' && (
                        <DatePickerField
                            key={`edit-${attribute.name}`}
                            name="editValue"
                            className="flex-1 min-w-0"
                            showTimeSelect={true}
                            timeIntervals={15}
                            dateFormat="dd.MM.yyyy HH:mm:ss"
                            timeFormat="HH:mm"
                            calendarStartDay={1}
                        />
                    )}
                    <ActionButton
                        icon={faCheck}
                        title={intl.formatMessage({id: 'button.save'})}
                        type="submit"
                        additionalClassname="text-xs p-1"
                    />
                    <ActionButton
                        icon={faTimes}
                        title={intl.formatMessage({id: 'button.cancel'})}
                        onClick={() => setEditingAttributeName(null)}
                        additionalClassname="text-xs p-1"
                    />
                </div>
            );
        }
    };

    const validationSchema = yup.object().shape({
        newAttributeName: yup.string(),
        newAttributeValue: yup.string()
    });

    const initialValues = React.useMemo(() => {
        // When editing, we need to provide the current value for the edit field
        const values: any = {
            newAttributeName: '',
            newAttributeValue: ''
        };

        // If we're editing an attribute, set the initial value for the edit form
        if (editingAttributeName) {
            const attribute = contact.attributes?.find(attr => attr.name === editingAttributeName);
            if (attribute) {
                values.editValue = attribute.value || '';
            }
        }

        return values;
    }, [editingAttributeName, contact.attributes]);

    const handleFormSubmit = (values: any, {resetForm}: any) => {
        // Handle editing existing attribute
        if (editingAttributeName) {
            const attributeType = getAttributeType(editingAttributeName) || 'STRING';
            let value: any;

            if (attributeType === 'BOOLEAN') {
                value = editingCheckboxValue;
            } else {
                value = values.editValue;
            }

            const stringValue = convertValueToString(value, attributeType);
            handleSetAttribute(editingAttributeName, stringValue);
            setEditingAttributeName(null);
            return;
        }

        // Handle adding new attribute
        if (selectedAttributeDefinition) {
            const selectedDef = getAvailableAttributeDefinitions().find(def => def.name === selectedAttributeDefinition);
            const attributeType = selectedDef?.type || 'STRING';

            // For non-boolean types, require a value
            if (attributeType !== 'BOOLEAN' && !values.newAttributeValue) {
                return;
            }

            const value = convertValueToString(values.newAttributeValue, attributeType);
            handleSetAttribute(selectedAttributeDefinition, value);
            setIsAddingAttribute(false);
            setSelectedAttributeDefinition('');
            resetForm();
        }
    };

    return (
        <Panel headline={intl.formatMessage({id: 'contact.attributes'})}>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleFormSubmit}
                enableReinitialize={true}
            >
                <Form>
                    {contact.attributes && contact.attributes.length > 0 && (
                        contact.attributes.map((attribute) => (
                            <div key={attribute.name} className="py-1 sm:py-2 sm:grid sm:grid-cols-3 sm:gap-4">
                                <dt className="text-sm font-medium text-gray-500 py-2">
                                    {getAttributeLabel(attribute.name)}
                                </dt>
                                <dd className="flex text-sm text-gray-900 sm:col-span-2 grow">
                                    <span className="grow">
                                        {renderAttributeValue(attribute)}
                                    </span>
                                </dd>
                            </div>
                        ))
                    )}

                    {isAddingAttribute && renderAddAttributeForm()}

                    {!contact.attributes || contact.attributes.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            {intl.formatMessage({id: 'contact.attributes.noAttributes'})}
                        </div>
                    ) : null}

                    <div className="pt-4 border-t border-gray-200">
                        {getAvailableAttributeDefinitions().length > 0 && !isAddingAttribute && (
                            <ActionButton
                                icon={faPlus}
                                label={intl.formatMessage({id: 'contact.attributes.addAttribute'})}
                                onClick={handleAddAttribute}
                            />
                        )}
                    </div>
                </Form>
            </Formik>
        </Panel>
    );
};

export default connector(ContactAttributesPanel);
